import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FranchisePartner } from './useFranchisePartnersData'
import { getMarketForCountry } from '../../../../constants/markets'

export type SortField = 'fp_code' | 'franchise_name' | 'state' | 'manager_count'
export type SortDirection = 'asc' | 'desc'

export interface Filters {
  textSearch: string
  market: string
  region: string
  bdm: string
  fpStatus: string
  systemStatus: string
  adoptionStatus: string
  icuStatus: string
  sellingStatus: string
}

export interface ActiveFilter {
  label: string
  value: string
}

interface UseFranchisePartnersFiltersReturn {
  filters: Filters
  updateFilter: (key: keyof Filters, value: string) => void
  clearFilters: () => void
  sortField: SortField
  sortDirection: SortDirection
  handleSort: (field: SortField) => void
  page: number
  setPage: (page: number) => void
  rowsPerPage: number
  setRowsPerPage: (rowsPerPage: number) => void
  filteredData: FranchisePartner[]
  paginatedData: FranchisePartner[]
  totalPages: number
  isInitialized: boolean
  hasActiveFilters: boolean
  getActiveFilterLabels: () => ActiveFilter[]
}

export const useFranchisePartnersFilters = (
  franchisePartners: FranchisePartner[]
): UseFranchisePartnersFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isInitialized, setIsInitialized] = useState(false)

  // Initialize filter and sort state from URL parameters
  // fpStatus sentinel 'all' → '' (explicit "All Statuses"), missing param → 'Active' default
  const getInitialFilters = (): Filters => {
    const fpStatusParam = searchParams.get('fpStatus')
    return {
      textSearch: searchParams.get('textSearch') || '',
      market: searchParams.get('market') || '',
      region: searchParams.get('region') || '',
      bdm: searchParams.get('bdm') || '',
      fpStatus: fpStatusParam === null ? 'Active' : fpStatusParam === 'all' ? '' : fpStatusParam,
      systemStatus: searchParams.get('systemStatus') || '',
      adoptionStatus: searchParams.get('adoptionStatus') || '',
      icuStatus: searchParams.get('icuStatus') || '',
      sellingStatus: searchParams.get('sellingStatus') || ''
    }
  }

  const getInitialSortField = (): SortField => {
    const field = searchParams.get('sortField')
    return (field === 'fp_code' || field === 'franchise_name' || field === 'state' || field === 'manager_count') ? field : 'franchise_name'
  }

  const getInitialSortDirection = (): SortDirection => {
    const dir = searchParams.get('sortDirection')
    return dir === 'desc' ? 'desc' : 'asc'
  }

  const getInitialPage = (): number => {
    const p = searchParams.get('page')
    return p ? parseInt(p, 10) : 1
  }

  const getInitialRowsPerPage = (): number => {
    const rpp = searchParams.get('perPage')
    return rpp ? parseInt(rpp, 10) : 25
  }

  // Filter and sort state
  const [filters, setFilters] = useState<Filters>(getInitialFilters())
  const [sortField, setSortField] = useState<SortField>(getInitialSortField())
  const [sortDirection, setSortDirection] = useState<SortDirection>(getInitialSortDirection())

  // Pagination state
  const [page, setPage] = useState(getInitialPage())
  const [rowsPerPage, setRowsPerPage] = useState(getInitialRowsPerPage())

  // Update URL parameters when filters, sort, or pagination changes
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true)
      return
    }

    const params = new URLSearchParams()

    // Add filter parameters
    if (filters.textSearch) params.set('textSearch', filters.textSearch)
    if (filters.market) params.set('market', filters.market)
    if (filters.region) params.set('region', filters.region)
    if (filters.bdm) params.set('bdm', filters.bdm)
    params.set('fpStatus', filters.fpStatus || 'all')
    if (filters.systemStatus) params.set('systemStatus', filters.systemStatus)
    if (filters.adoptionStatus) params.set('adoptionStatus', filters.adoptionStatus)
    if (filters.icuStatus) params.set('icuStatus', filters.icuStatus)
    if (filters.sellingStatus) params.set('sellingStatus', filters.sellingStatus)

    // Add sort parameters
    if (sortField !== 'franchise_name') params.set('sortField', sortField)
    if (sortDirection !== 'asc') params.set('sortDirection', sortDirection)

    // Add pagination parameters
    if (page !== 1) params.set('page', page.toString())
    if (rowsPerPage !== 25) params.set('perPage', rowsPerPage.toString())

    // Update URL without triggering navigation
    setSearchParams(params, { replace: true })
  }, [filters, sortField, sortDirection, page, rowsPerPage, isInitialized, setSearchParams])

  // Filter and sort franchise partners
  const filteredData = useMemo(() => {
    let filtered = franchisePartners.filter(fp => {
      // Text search filter (franchise name or FP code)
      const searchMatch = !filters.textSearch ||
        fp.franchise_name.toLowerCase().includes(filters.textSearch.toLowerCase()) ||
        fp.fp_code.toLowerCase().includes(filters.textSearch.toLowerCase())

      // Market filter
      const fpMarket = fp.market || getMarketForCountry(fp.country)
      const marketMatch = !filters.market || fpMarket === filters.market

      // Region filter
      const regionMatch = !filters.region || fp.region_code === filters.region

      // BDM filter (matches bdm_id)
      const bdmMatch = !filters.bdm || fp.bdm_id === parseInt(filters.bdm, 10)

      // FP Status filter
      const fpStatusMatch = !filters.fpStatus || fp.fp_status === filters.fpStatus

      // System Status filter
      const systemStatusMatch = !filters.systemStatus || fp.system_status === filters.systemStatus

      // Adoption Status filter
      const adoptionMatch = !filters.adoptionStatus || (fp.adoption_status || 'Unknown') === filters.adoptionStatus

      // ICU Status filter
      const icuMatch = !filters.icuStatus ||
        (filters.icuStatus === 'yes' && fp.is_icu === true) ||
        (filters.icuStatus === 'no' && fp.is_icu !== true)

      // Selling Status filter
      const sellingMatch = !filters.sellingStatus ||
        (filters.sellingStatus === 'yes' && fp.is_selling === true) ||
        (filters.sellingStatus === 'no' && fp.is_selling !== true)

      return searchMatch && marketMatch && regionMatch && bdmMatch && fpStatusMatch && systemStatusMatch && adoptionMatch && icuMatch && sellingMatch
    })

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortField]
      let bValue = b[sortField]

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [franchisePartners, filters, sortField, sortDirection])

  // Paginate the filtered results
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, page, rowsPerPage])

  // Calculate total pages
  const totalPages = Math.ceil(filteredData.length / rowsPerPage)

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const updateFilter = (key: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPage(1) // Reset to first page when filters change
  }

  const clearFilters = () => {
    setFilters({
      textSearch: '',
      market: '',
      region: '',
      bdm: '',
      fpStatus: '',
      systemStatus: '',
      adoptionStatus: '',
      icuStatus: '',
      sellingStatus: ''
    })
    setPage(1)
  }

  const handleSetRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
    setPage(1)
  }

  const hasActiveFilters =
    filters.textSearch !== '' ||
    filters.market !== '' ||
    filters.region !== '' ||
    filters.bdm !== '' ||
    filters.fpStatus !== '' ||
    filters.systemStatus !== '' ||
    filters.adoptionStatus !== '' ||
    filters.icuStatus !== '' ||
    filters.sellingStatus !== ''

  const getActiveFilterLabels = (): ActiveFilter[] => {
    const labels: ActiveFilter[] = []
    if (filters.textSearch) labels.push({ label: 'Search', value: filters.textSearch })
    if (filters.market) labels.push({ label: 'Market', value: filters.market })
    if (filters.region) labels.push({ label: 'Region', value: filters.region })
    if (filters.bdm) labels.push({ label: 'ROM/FBC', value: filters.bdm })
    if (filters.fpStatus) labels.push({ label: 'FP Status', value: filters.fpStatus })
    if (filters.systemStatus) labels.push({ label: 'Platform Status', value: filters.systemStatus })
    if (filters.adoptionStatus) labels.push({ label: 'Platform Adoption', value: filters.adoptionStatus })
    if (filters.icuStatus) labels.push({ label: 'ICU', value: filters.icuStatus })
    if (filters.sellingStatus) labels.push({ label: 'Selling', value: filters.sellingStatus })
    return labels
  }

  return {
    filters,
    updateFilter,
    clearFilters,
    sortField,
    sortDirection,
    handleSort,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage: handleSetRowsPerPage,
    filteredData,
    paginatedData,
    totalPages,
    isInitialized,
    hasActiveFilters,
    getActiveFilterLabels
  }
}